import { ChangeEvent } from 'react';
import { Calendar, CalendarClock, Check, Repeat } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@Utils';

import { Input } from '@Components/ui-v2/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@Components/ui-v2/select';
import StepHeader from '../components/stepHeader';
import {
  cadenceUsesWeekdays,
  END_DATE_PRESET_WEEKS,
  getFullWeekdayOptions,
  getTodayIso,
  getWeekdayOptions,
  MONTHLY_ORDINAL_OPTIONS,
  SECTION_LABEL_CLASS_NAME,
} from '../constants';
import {
  ScheduleWizardFrequencyState,
  WizardCadence,
  WizardEndDateMode,
  WizardMonthlyOrdinal,
} from '../scheduleWizard.types';

interface Step3FrequencyProps {
  value: ScheduleWizardFrequencyState;
  onChange: (value: ScheduleWizardFrequencyState) => void;
  hideHeader?: boolean;
}

const CADENCE_OPTIONS: { value: WizardCadence; labelKey: string; descKey: string; icon: typeof Repeat }[] = [
  {
    value: 'once',
    labelKey: 'Schedule.Wizard.Frequency.CadenceOnce',
    descKey: 'Schedule.Wizard.Frequency.CadenceOnceDesc',
    icon: Calendar,
  },
  {
    value: 'weekly',
    labelKey: 'Schedule.Wizard.Frequency.CadenceWeekly',
    descKey: 'Schedule.Wizard.Frequency.CadenceWeeklyDesc',
    icon: Repeat,
  },
  {
    value: 'bi_weekly',
    labelKey: 'Schedule.Wizard.Frequency.CadenceBiWeekly',
    descKey: 'Schedule.Wizard.Frequency.CadenceBiWeeklyDesc',
    icon: Repeat,
  },
  {
    value: 'monthly',
    labelKey: 'Schedule.Wizard.Frequency.CadenceMonthly',
    descKey: 'Schedule.Wizard.Frequency.CadenceMonthlyDesc',
    icon: CalendarClock,
  },
];

const END_DATE_MODE_OPTIONS: { value: WizardEndDateMode; labelKey: string }[] = [
  { value: 'none', labelKey: 'Schedule.Wizard.Frequency.EndDateNone' },
  { value: 'preset', labelKey: 'Schedule.Wizard.Frequency.EndDatePreset' },
  { value: 'custom', labelKey: 'Schedule.Wizard.Frequency.EndDateCustom' },
];

const chipClassName = (active: boolean): string =>
  cn(
    'flex h-10 min-w-[46px] items-center justify-center rounded-[9px] border px-3 text-xs font-semibold transition-colors',
    active
      ? 'border-brand-600 bg-brand-600 text-white'
      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300',
  );

const formatVisitDate = (isoDate: string, locale: string): string =>
  new Intl.DateTimeFormat(locale, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }).format(
    new Date(`${isoDate}T00:00:00`),
  );

const Step3Frequency = ({ value, onChange, hideHeader = false }: Step3FrequencyProps) => {
  const { t, i18n } = useTranslation();
  const weekdayOptions = getWeekdayOptions(i18n.language);
  const fullWeekdayOptions = getFullWeekdayOptions(i18n.language);
  const formattedVisitDate = value.startDate ? formatVisitDate(value.startDate, i18n.language) : null;

  const monthlyOrdinalLabelKey =
    MONTHLY_ORDINAL_OPTIONS.find((option) => option.value === value.monthlyOrdinal)?.labelKey ??
    MONTHLY_ORDINAL_OPTIONS[1].labelKey;
  const monthlyWeekdayLabel =
    fullWeekdayOptions.find((option) => option.value === value.monthlyWeekday)?.label ?? fullWeekdayOptions[0].label;
  const monthlyOrdinalNote = t('Schedule.Wizard.Frequency.MonthlyOrdinalNote', {
    ordinalLabel: t(monthlyOrdinalLabelKey),
    weekday: monthlyWeekdayLabel,
  });

  const handleCadenceChange = (cadence: WizardCadence) => {
    if (cadence === value.cadence) return;
    onChange({
      ...value,
      cadence,
      weekdays: cadenceUsesWeekdays(cadence) ? value.weekdays : [],
    });
  };

  const toggleWeekday = (day: number) => {
    const weekdays = value.weekdays.includes(day)
      ? value.weekdays.filter((existing) => existing !== day)
      : [...value.weekdays, day].sort((a, b) => a - b);
    onChange({ ...value, weekdays });
  };

  const handleMonthlyOrdinalChange = (nextOrdinal: string) => {
    onChange({ ...value, monthlyOrdinal: nextOrdinal as WizardMonthlyOrdinal });
  };

  const handleMonthlyWeekdayChange = (nextWeekday: string) => {
    onChange({ ...value, monthlyWeekday: Number(nextWeekday) });
  };

  const handleStartDateChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange({ ...value, startDate: event.target.value || null });
  };

  const handleEndDateModeChange = (endDateMode: WizardEndDateMode) => {
    if (endDateMode === value.endDateMode) return;
    onChange({
      ...value,
      endDateMode,
      endDatePresetWeeks: endDateMode === 'preset' ? value.endDatePresetWeeks : null,
      endDate: endDateMode === 'custom' ? value.endDate : null,
    });
  };

  const handleEndDateCustomChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange({ ...value, endDate: event.target.value || null });
  };

  return (
    <div className="flex flex-col gap-5">
      {!hideHeader && (
        <StepHeader
          title={t('Schedule.Wizard.Steps.frequency')}
          subtitle={t('Schedule.Wizard.Frequency.SectionSubtitle')}
        />
      )}

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {CADENCE_OPTIONS.map((option) => {
          const isSelected = value.cadence === option.value;
          const Icon = option.icon;
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={isSelected}
              onClick={() => handleCadenceChange(option.value)}
              className={cn(
                'flex flex-col gap-2 rounded-lg border px-3.5 py-3 text-left transition-colors',
                isSelected
                  ? 'border-brand-600 bg-brand-50 ring-2 ring-brand-100'
                  : 'border-gray-200 bg-white hover:border-gray-300',
              )}>
              <div className="flex items-center justify-between">
                <span
                  className={cn(
                    'flex h-[34px] w-[34px] items-center justify-center rounded-lg',
                    isSelected ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-400',
                  )}>
                  <Icon size={16} />
                </span>
                <span
                  className={cn(
                    'flex h-5 w-5 items-center justify-center rounded-full border',
                    isSelected ? 'border-brand-600 bg-brand-600 text-white' : 'border-gray-300 bg-white',
                  )}>
                  {isSelected && <Check size={12} strokeWidth={3} />}
                </span>
              </div>
              <span className={cn('text-sm font-semibold', isSelected ? 'text-brand-700' : 'text-gray-900')}>
                {t(option.labelKey)}
              </span>
              <span className="text-xs font-normal leading-snug text-gray-500">{t(option.descKey)}</span>
            </button>
          );
        })}
      </div>

      {value.cadence === 'once' ? (
        <div className="flex flex-col gap-1.5 rounded-xl border border-gray-200 bg-white p-5">
          <span className={SECTION_LABEL_CLASS_NAME}>{t('Schedule.Wizard.Frequency.VisitDateLabel')}</span>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              type="date"
              required
              min={getTodayIso()}
              value={value.startDate ?? ''}
              onChange={handleStartDateChange}
              className="w-48"
            />
            {formattedVisitDate && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-brand-200 bg-brand-50 px-3 py-1 text-xs font-semibold text-brand-700">
                <Calendar size={12} />
                {formattedVisitDate}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500">{t('Schedule.Wizard.Frequency.VisitDateNote')}</p>
        </div>
      ) : (
        <div className="flex flex-col gap-5 rounded-xl border border-gray-200 bg-white p-5">
          <div className="flex flex-col gap-1.5">
            <span className={SECTION_LABEL_CLASS_NAME}>{t('Schedule.Wizard.Frequency.StartDateLabel')}</span>
            <Input
              type="date"
              required
              value={value.startDate ?? ''}
              onChange={handleStartDateChange}
              className="w-48"
            />
          </div>

          {cadenceUsesWeekdays(value.cadence) ? (
            <div className="flex flex-col gap-1.5 border-t border-gray-100 pt-5">
              <span className={SECTION_LABEL_CLASS_NAME}>{t('Schedule.Wizard.Frequency.WeekdaysLabel')}</span>
              <div className="flex flex-wrap gap-1.5">
                {weekdayOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => toggleWeekday(option.value)}
                    className={chipClassName(value.weekdays.includes(option.value))}>
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-1.5 border-t border-gray-100 pt-5">
              <span className={SECTION_LABEL_CLASS_NAME}>{t('Schedule.Wizard.Frequency.MonthlyRepeatsOnLabel')}</span>
              <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
                <span>{t('Schedule.Wizard.Frequency.MonthlyOrdinalPrefix')}</span>
                <Select value={value.monthlyOrdinal} onValueChange={handleMonthlyOrdinalChange}>
                  <SelectTrigger className="h-9 w-auto gap-2 rounded-md border-gray-200 bg-white px-2.5 text-sm font-semibold text-gray-900">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHLY_ORDINAL_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {t(option.labelKey)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={String(value.monthlyWeekday)} onValueChange={handleMonthlyWeekdayChange}>
                  <SelectTrigger className="h-9 w-auto gap-2 rounded-md border-gray-200 bg-white px-2.5 text-sm font-semibold text-gray-900">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {fullWeekdayOptions.map((option) => (
                      <SelectItem key={option.value} value={String(option.value)}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span>{t('Schedule.Wizard.Frequency.MonthlyOrdinalSuffix')}</span>
              </div>
              <p className="text-xs font-semibold text-brand-700">{monthlyOrdinalNote}</p>
            </div>
          )}

          <div className="flex flex-col gap-2 border-t border-gray-100 pt-5">
            <span className={SECTION_LABEL_CLASS_NAME}>{t('Schedule.Wizard.Frequency.EndDateLabel')}</span>
            {END_DATE_MODE_OPTIONS.map((option) => (
              <div key={option.value} className="flex flex-col gap-2">
                <label
                  htmlFor={`schedule-wizard-end-date-mode-${option.value}`}
                  className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                  <input
                    id={`schedule-wizard-end-date-mode-${option.value}`}
                    type="radio"
                    name="schedule-wizard-end-date-mode"
                    checked={value.endDateMode === option.value}
                    onChange={() => handleEndDateModeChange(option.value)}
                    className="h-4 w-4 accent-brand-600"
                  />
                  {t(option.labelKey)}
                </label>

                {option.value === 'preset' && value.endDateMode === 'preset' && (
                  <div className="ml-6 flex flex-wrap gap-1.5">
                    {END_DATE_PRESET_WEEKS.map((weeks) => (
                      <button
                        key={weeks}
                        type="button"
                        onClick={() => onChange({ ...value, endDatePresetWeeks: weeks })}
                        className={chipClassName(value.endDatePresetWeeks === weeks)}>
                        {t('Schedule.Wizard.Frequency.EndDatePreset')}{' '}
                        {t('Schedule.Wizard.Frequency.WeeksSuffix', { count: weeks })}
                      </button>
                    ))}
                  </div>
                )}

                {option.value === 'custom' && value.endDateMode === 'custom' && (
                  <div className="ml-6">
                    <Input
                      type="date"
                      required
                      min={
                        value.startDate !== null && value.startDate > getTodayIso() ? value.startDate : getTodayIso()
                      }
                      value={value.endDate ?? ''}
                      onChange={handleEndDateCustomChange}
                      className="w-48"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Step3Frequency;
